import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import {
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    Switch,
    IconButton,
    Tooltip,
    Button,
    Tabs,
    Tab,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import { AlertDialog } from 'Shared/AlertDialog';
import FlowsGraph from 'Shared/flows/FlowsGraph';
import FlowPerformanceGraph from 'Shared/flows/FlowPerformanceGraph';
import FlowCopyForm from 'Shared/flows/flows-interface/FlowCopyForm';
import { CopyIcon } from 'Shared/Icons';
import { Box } from "@mui/system";
import { setDragData } from 'Shared/flows/folders/folderUtils';
import FlowFolderLabel from 'Shared/flows/folders/FlowFolderLabel';
import http from 'Utils/http';
import FlowIdLabel from 'Shared/flows/FlowIdLabel';
import FlowRunButton from 'Shared/flows/FlowRunButton';

export const MyFlowsItem = ({item, needUpdate,  unlinkFlow, deleteFlow, userRole, selected, onToggleSelect, folderPath}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = useSelector(state => state.auth?.user);

    const [expanded, setExpanded] = useState(false);
    const [visibleCopyFlowForm, setVisibleCopyFlowForm] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [confirmButtonText, setConfirmButtonText] = useState('Yes');
    const [skipButtonText, setSkipButtonText] = useState('No');
    const [modalAction, setModalAction] = useState('change');
    const [modalTitle, setModalTitle] = useState('Confirm your action');
    const [modalText, setModalText] = useState(<span>Change <strong>{item.name}</strong> availability?</span>);


    const toggleExpanded = (e) => {
        e.stopPropagation();
        setExpanded((prev) => (prev === 'panel1' ? false : 'panel1'));
    };

    const handleChangeSwitch = (e) => {
        e.preventDefault();
        if (currentUser?.company_id) {
            const nextStatus = item.is_active_for_any_company ? 'disabled' : 'active';
            setModalTitle('Confirm your action');
            setModalText(<span>{nextStatus === 'active' ? 'Enable' : 'Disable'} <strong>{item.name}</strong> access for your company?</span>);
            setConfirmButtonText('Yes');
            setSkipButtonText('No');
            setModalAction('access');
            setIsActiveModal(true);
        }
    };

    const toggleCompanyAccess = () => {
        const nextStatus = item.is_active_for_any_company ? 'disabled' : 'active';
        const params = {
            companies: [
                {
                    company_id: currentUser.company_id,
                    status: nextStatus
                }
            ]
        };

        dispatch(setLoading(true));
        http.post(`/flow/${item.id}/company-access`, params)
            .then(() => {
                if (needUpdate) {
                    needUpdate(true);
                }
                enqueueSnackbar(`Flow access has been ${nextStatus === 'active' ? 'enabled' : 'disabled'}`, {
                    action: CustomSnackbar,
                    variant: 'success'
                });
            })
            .catch((err) => {
                enqueueSnackbar(`${err.response?.data?.message || 'Error updating flow access'}`, {
                    action: CustomSnackbar,
                    variant: 'error'
                });
            })
            .finally(() => {
                dispatch(setLoading(false));
            });
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        setModalTitle(`Delete "${item.name}"?`);
        setModalText(<span>Are you sure you want to delete <strong>{item.name}</strong>?</span>);
        setConfirmButtonText('Delete');
        setSkipButtonText('Cancel');
        setModalAction('delete');
        setIsActiveModal(true);
    };

    const handleCopy = (e) => {
        e.stopPropagation();
        setVisibleCopyFlowForm(true);
    };

    const handleUnlink = (e) => {
        e.stopPropagation();
        setModalTitle(`Delete "${item.name}"?`);
        setModalText(<span>Are you sure you want to delete <strong>{item.name}</strong>?</span>);
        setConfirmButtonText('Delete');
        setSkipButtonText('Cancel');
        setModalAction('unlink');
        setIsActiveModal(true);
    };

    const [isActiveModal, setIsActiveModal] = useState(false);
    const onClose = () => setIsActiveModal(false);
    const onAgree = () => {
        setIsActiveModal(false);
        if (modalAction === 'unlink') {
            unlinkFlow(item);
        }
        if (modalAction === 'delete') {
            deleteFlow(item);
        }
        if (modalAction === 'access') {
            toggleCompanyAccess();
        }
    };

    const toFlow = (event, id) => {
        event.preventDefault();
        navigate(`/flow/${id}`);
    };

    const skipButton = modalAction === 'unlink' ? (
        <Button
            sx={{ backgroundColor: '#e9e9e9', color: '#707070', borderRadius: '5px' }}
            onClick={onClose}
        >
            {skipButtonText}
        </Button>
    ) : null;

    const confirmButton = modalAction === 'unlink' ? (
        <Button
            sx={{
                backgroundColor: '#f44336',
                color: '#fff',
                borderRadius: '5px',
                '&:hover': { backgroundColor: '#bf2317' },
            }}
            onClick={onAgree}
        >
            {confirmButtonText}
        </Button>
    ) : null;

    return (
        <>
            <AlertDialog
                visible={isActiveModal}
                onClose={onClose}
                onAgree={onAgree}
                text={modalText}
                title={modalTitle}
                noButton={skipButton}
                yesButton={confirmButton}
                yesButtonText={confirmButtonText}
                noButtonText={skipButtonText}
            />
            <FlowCopyForm id={item.id} flowName={item.name} needUpdateList={needUpdate} setVisibleFlowForm={setVisibleCopyFlowForm} visibleFlowForm={visibleCopyFlowForm}/>
            <Accordion
                sx={{
                    marginBottom: '20px',
                }}
                expanded={expanded === 'panel1'}
            >
                <AccordionSummary
                    draggable
                    onDragStart={(e) => setDragData(e, { kind: 'flow', id: item.id })}
                    sx={{
                        backgroundColor: '#F2F5F8',
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                        },
                    }}
                    expandIcon={
                        <Tooltip title={
                            <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Edit Flow</span>
                            } arrow>
                            <IconButton
                                onClick={(e) => {
                                    toFlow(e, item.id);
                                }}
                            >
                                <SettingsIcon/>
                            </IconButton>
                        </Tooltip>
                    }
                >
                    {onToggleSelect && (
                        <Checkbox
                            checked={selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onToggleSelect(item)}
                            sx={{ marginRight: '8px', flexShrink: 0 }}
                        />
                    )}
                    <FlowIdLabel id={item.id} externalId={item.external_id} />
                    <Box
                        sx={{ flex: 1, minWidth: 0, marginRight: '20px', cursor: 'pointer' }}
                        onClick={(e) => toFlow(e, item.id)}
                    >
                        <FlowFolderLabel path={folderPath} />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#3B465C',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {item?.name}
                            {item?.company_name && (
                                <span style={{ fontSize: '0.85rem', color: '#888', fontWeight: 400, marginLeft: 6 }}>
                                    ({item.company_name})
                                </span>
                            )}
                        </Typography>
                        {item?.description && item.description !== item.name && (
                            <Typography
                                variant="body2"
                                sx={{ color: '#888', fontSize: '0.8rem', overflowWrap: 'anywhere' }}
                            >
                                {item.description}
                            </Typography>
                        )}
                    </Box>

                    {item.access === 'private' && userRole !== 'User' ? (
                        <>
                            <Tooltip title={
                                <span style={{fontSize: '1rem', lineHeight: '120%'}}>Make the flow visible to company users</span>
                            } arrow>
                                <Switch
                                    sx={{
                                        marginLeft: 'auto',
                                    }}
                                    checked={item.is_active_for_any_company || false}
                                    color="primary"
                                    onClick={(e) => handleChangeSwitch(e)}
                                />
                            </Tooltip>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Usage Statistics</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: expanded ? '#F4B156' : '',
                                    }}
                                    aria-label="open-chart"
                                    onClick={toggleExpanded}
                                >
                                    <StackedBarChartIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Copy Flow</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: '#f44336',
                                    }}
                                    onClick={(e) => {
                                        handleCopy(e);
                                    }}
                                    aria-label="copy"
                                >
                                    <CopyIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Delete Flow</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: '#f44336',
                                    }}
                                    onClick={(e) => {
                                        handleDelete(e);
                                    }}
                                    aria-label="delete"
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Box
                            sx={{
                                marginLeft: 'auto',
                                marginRight: '14px'
                            }}>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Usage Statistics</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: expanded ? '#F4B156' : '',
                                    }}
                                    aria-label="open-chart"
                                    onClick={toggleExpanded}
                                >
                                    <StackedBarChartIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Copy Flow</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: '#f44336',
                                    }}
                                    onClick={(e) => {
                                        handleCopy(e);
                                    }}
                                    aria-label="copy"
                                >
                                    <CopyIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Delete Flow</span>
                            } arrow>
                                <IconButton
                                    sx={{
                                        marginRight: '14px',
                                        color: '#f44336',
                                    }}
                                    onClick={(e) => {
                                        handleUnlink(e);
                                    }}
                                    aria-label="delete"
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <FlowRunButton flow={item} />
                </AccordionSummary>
                <AccordionDetails>
                    {expanded ? (
                        <Box>
                            <Tabs
                                value={selectedTab}
                                onChange={(e, newValue) => setSelectedTab(newValue)}
                                sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}
                            >
                                <Tab label="API Requests" />
                                <Tab label="Performance" />
                            </Tabs>
                            {selectedTab === 0 && <FlowsGraph item={item}/>}
                            {selectedTab === 1 && <FlowPerformanceGraph item={item}/>}
                        </Box>
                    ) : null}
                </AccordionDetails>
            </Accordion>
        </>
    );
};
